// Legacy Node WebSocket client (doi_gov_api ws-notification-server).

import { envConfig } from "../utils/config";
import { handleIncomingNotification } from "./notification.helpers";

function buildWebSocketUrl(baseUrl: string, userId?: string): string {
    const base = baseUrl.replace(/\/$/, "");
    if (!userId) return base;
    const sep = base.includes("?") ? "&" : "?";
    return `${base}${sep}userId=${encodeURIComponent(userId)}`;
}

const createNotificationSocket = () => {
    let socket: WebSocket | null = null;
    let reconnectTimeout: number | null = null;
    let shouldReconnect = true;
    let retryCount = 0;

    const getReconnectDelay = () => {
        const baseDelay = 2000;
        const maxDelay = 30000;
        const delay = Math.min(baseDelay * 2 ** retryCount, maxDelay);
        retryCount += 1;
        return delay;
    };

    const connect = (userId?: string) => {
        if (!shouldReconnect || !userId) return;

        if (
            socket &&
            (socket.readyState === WebSocket.OPEN ||
                socket.readyState === WebSocket.CONNECTING)
        ) {
            return;
        }

        const wsUrl = buildWebSocketUrl(envConfig.APP_WEBSOCKET_URL, userId);
        socket = new WebSocket(wsUrl);

        socket.onopen = () => {
            retryCount = 0;

            if (reconnectTimeout) {
                clearTimeout(reconnectTimeout);
                reconnectTimeout = null;
            }
        };

        socket.onmessage = (event) => {
            try {
                handleIncomingNotification(JSON.parse(event.data));
            } catch {
                console.error("Invalid WS JSON:", event.data);
            }
        };

        socket.onclose = () => {
            if (!shouldReconnect) return;

            const delay = getReconnectDelay();
            reconnectTimeout = window.setTimeout(() => {
                connect(userId);
            }, delay);
        };

        socket.onerror = () => {
            // onclose handles reconnect
        };
    };

    const disconnect = () => {
        shouldReconnect = false;

        if (socket) {
            socket.close();
            socket = null;
        }

        if (reconnectTimeout) {
            clearTimeout(reconnectTimeout);
            reconnectTimeout = null;
        }
    };

    const reconnect = (userId?: string) => {
        shouldReconnect = true;
        connect(userId);
    };

    const getConnectionStatus = (): string => {
        if (!socket) return "Not connected";
        switch (socket.readyState) {
            case WebSocket.CONNECTING: return "Connecting";
            case WebSocket.OPEN: return "Connected";
            case WebSocket.CLOSING: return "Closing";
            case WebSocket.CLOSED: return "Closed";
            default: return "Unknown";
        }
    };

    return {
        connect,
        disconnect,
        reconnect,
        getConnectionStatus,
    };
};

export const notificationSocket = createNotificationSocket();
